/**
 * Document service for document upload and management API calls.
 */
import { AxiosError } from 'axios';

import api from './api';

interface UploadResponse {
    job_id: string;
    doc_id: string;
    duplicate?: boolean;
    /**
     * #1600 — true when the upload matched a soft-deleted document and the
     * backend RESTORED it (cleared ``deleted_at``) instead of creating a new
     * row or rejecting the upload. No ``job_id`` accompanies a restore (the
     * document's content is unchanged — nothing reprocesses).
     *
     * #1607 — also true when the upload matched a soft-deleted **multi-document
     * bundle** whose children were ALL soft-deleted: the backend resurrected the
     * children (cleared each child's ``deleted_at``). The UI surfaces the same
     * "wiederhergestellt" feedback as a single-doc restore.
     */
    restored?: boolean;
    /**
     * #1607 — when a soft-deleted multi-document bundle was resurrected, the
     * number of child documents whose ``deleted_at`` was cleared. Present only
     * on the bundle-resurrection path (alongside ``restored: true``).
     */
    resurrected_children?: number;
}

/**
 * Thrown by ``documentService.upload`` when the backend rejects an
 * upload with HTTP 409 because an identical file (same SHA-256
 * content hash) is already in the user's tenant. Carries the
 * original filename and the existing doc_id so the UI can show a
 * meaningful toast and optionally link to the existing document.
 */
export class DuplicateUploadError extends Error {
    readonly filename: string;
    readonly existingDocId?: string;
    constructor(filename: string, existingDocId?: string) {
        super(`Duplicate upload: ${filename}`);
        this.name = 'DuplicateUploadError';
        this.filename = filename;
        this.existingDocId = existingDocId;
    }
}

interface JobStatus {
    job_id: string;
    doc_id: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress: {
        current_step: string | null;
        step_status: 'queued' | 'processing' | null;
        steps_completed: number;
        total_steps: number;
    };
    created_at: string;
    completed_at: string | null;
    error_message: string | null;
}

/**
 * Parent source info for documents that were split from a multi-document PDF.
 */
interface ParentSource {
    parent_doc_id: string;
    parent_filename: string;
    page_range: string;
    page_range_display: string;
}

interface Document {
    doc_id: string;
    filename: string;
    smart_filename: string | null;
    document_type: string | null;
    description: string | null;
    keyword: string | null;
    document_date: string | null;
    uploaded_at: string;
    // #1965 B2b-prep: dedicated split-parent marker (UDB 0051). Optional — absent
    // on older payloads; consumers dual-read it with the document_type sentinel.
    is_multi_document?: boolean;
    confidence: number | null;
    cloud_sync: Record<string, string> | null;
    // Document feed status
    status: 'processing' | 'completed' | 'failed';
    /** Present only for documents split from a multi-document PDF */
    parent_source?: ParentSource;
    /** Error message if document processing failed */
    error_message?: string | null;
    /** Job ID for tracking processing */
    job_id?: string | null;
    // Taxonomy-based classification (from INDEX.yaml)
    category?: string | null;
    subcategory?: string | null;
    tier?: number | null;
    // taxonomy-v2 native cell (#1373) — present on docs carrying a (domain, role)
    // cell (v3-native or v1→v2 translated). The German display labels are
    // resolved backend-side from config/taxonomy-v2.
    domain?: string | null;
    role?: string | null;
    domain_name?: string | null;
    role_name?: string | null;
    // Feed-row icon (lucide-react icon name). #2009: resolved server-side by the
    // backend icon_service from the native taxonomy-v2 role (get_role_icon) — the
    // legacy config/taxonomy/input doctype-YAML lookup was removed with classifier-v1.
    icon?: string | null;
    // Display name from document type YAML displayName field
    document_type_display_name?: string | null;
    // Pre-computed highlight bubbles for folded view (JSON array)
    // Format: [{"label": "Schlag", "value": "1570", "style": "default"}, ...]
    highlighted_facts?: string | null;
    // Human-readable processing status for page refresh recovery
    // Format: "Analyzing...", "Retry #2: Analyzing...", "" (empty when complete)
    processing_status?: string | null;
    // User-defined tags (JSON array of tag IDs)
    tags?: string | null;
}

/** Subcategory with count and German name from backend */
interface SubcategoryCount {
    count: number;
    name: string;
}

/** Category counts structure from backend */
interface CategoryCount {
    count: number;
    name: string;
    subcategories: Record<string, SubcategoryCount>;
}

interface ListDocumentsResponse {
    documents: Document[];
    offset: number;
    limit: number;
    total: number;
    /** Total documents before filtering (INCLUDES ``filtered`` docs; load-bearing count) */
    total_all: number;
    /**
     * #1889 — total non-``filtered`` documents (for the "Alle Dokumente" count
     * in the sidebar / tile grid, so the badge equals the feed). Optional for
     * backward compatibility with a backend that predates the key.
     */
    total_all_visible?: number;
    new_docs_count: number;
    /**
     * classifier-v3 (#1066): native (domain, role) lens counts,
     * ``{domain: {count, name, subcategories: {role: {count, name}}}}``. The sole
     * lens-count source since the classifier-v1 decommission (#2009) removed the
     * legacy ``category_counts`` GROUP BY chain.
     */
    domain_counts?: Record<string, CategoryCount>;
}

type SortBy = 'document_date' | 'received_date';
/** `unlinked` (FR-017 #7a): processed documents with no live twin link. */
type ViewMode = 'all' | 'new' | 'unlinked';

interface ListDocumentsParams {
    offset?: number;
    limit?: number;
    sort_by?: SortBy;
    view?: ViewMode;
    /** classifier-v3 (#1066): native taxonomy-v2 lens filter axes. The legacy
     *  category/subcategory/document_type filters were removed with the
     *  classifier-v1 decommission (#2009). */
    domain?: string;
    role?: string;
    search?: string;
    /** Filter by tag ID */
    tag?: string;
    /** If true, fetch only soft-deleted documents */
    deleted_only?: boolean;
}

/** Filters for resolving a deep-linked doc to its feed page (#1187). Mirror the
 * active feed query so the resolved page matches what the feed renders. */
export interface ResolveDocumentPageParams {
    /** Feed page size — the offset is divided by this to compute the page. */
    page_size?: number;
    sort_by?: SortBy;
    view?: ViewMode;
    /** classifier-v3 (#2009): native taxonomy-v2 lens axes. Replaced the legacy
     * category/subcategory/document_type filters (frozen NULL post write-freeze). */
    domain?: string;
    role?: string;
    search?: string;
    /** Filter by tag ID — forwarded so the resolved page matches a tag-filtered feed. */
    tag?: string;
    deleted_only?: boolean;
}

/** Response from ``GET /user-documents/{id}/resolve-page`` (#1187). ``page`` is
 * 0-indexed (matches ``useDocuments.currentPage``); both ``offset`` and ``page``
 * are null when ``found`` is false. */
export interface ResolveDocumentPageResponse {
    doc_id: string;
    offset: number | null;
    page: number | null;
    found: boolean;
}

/**
 * Per-field validation finding emitted by the worker's L0 doc-side
 * validator (issue #620 subtask 06). The block is keyed by ``field_id``
 * and projected through ``GET /user-documents/{id}/metadata`` as a
 * sibling of ``extracted_facts``.
 *
 * Severity vocabulary mirrors the worker side
 * (``worker/src/steps/extract_facts.py:_compute_validation_block``):
 * - ``hard``    — extraction violated a non-negotiable rule (e.g. empty
 *                 required field). Operator must correct before downstream
 *                 entity linking trusts the value.
 * - ``soft``    — value smells wrong (heuristic mismatch, format hint).
 *                 Operator review recommended; pipeline still proceeds.
 * - ``autofix`` — validator suggests a deterministic replacement. The
 *                 ``autofix_value`` field carries the suggested value.
 */
export type ValidationSeverity = 'hard' | 'soft' | 'autofix';

export interface FieldValidation {
    severity: ValidationSeverity;
    message: string;
    validator_id: string;
    autofix_value?: unknown;
}

export interface DocumentMetadata {
    doc_id: string;
    document_date: string | null;
    /**
     * Extracted facts keyed by field id. ``value`` is normally a scalar string, but a v3 cell's
     * repeating-row field (``line_items`` — the Buchungen on a bank statement, #1498) arrives as a
     * raw array of row objects. The documents page renders array values as a short summary for now;
     * a dedicated repeating-row (Buchungen) table renderer is a tracked follow-up.
     */
    extracted_facts: Record<string, { value: string | unknown[]; label?: string; overridden?: boolean }>;
    /**
     * FR-017 #5: the extracted-facts key the document's rows live under, as the extraction
     * recorded it. Absent/``null`` → ``line_items`` (documents extracted before it was recorded;
     * ``payment-advice`` uses ``referenced_documents``). Read through ``documentRows``.
     */
    line_items_field?: string | null;
    manually_classified: boolean;
    classification: {
        category: string | null;
        subcategory: string | null;
        document_type: string | null;
        /**
         * classifier-v3 native two-axis cell + version (#1066 / SC7). Optional/nullable:
         * absent (``null``/``undefined``) for legacy v1 docs whose sidecar never wrote them —
         * the UI then falls back to the legacy triple above.
         */
        domain?: string | null;
        role?: string | null;
        classifier_version?: 'v1' | 'v2' | 'v3' | null;
        /**
         * classifier-path provenance tag (#1373). ``"legacy_translation"`` marks a v1→v2
         * translated cell (a v1 guess); ``null``/absent for native v3 / gold / untranslated docs.
         */
        classifier_path?: string | null;
    };
    /**
     * L0 doc-side validation findings keyed by ``field_id``. Empty for
     * documents predating the ``L0_DOC_VALIDATION_ENABLED`` flag — the
     * backend coerces a missing key to ``{}`` so the FE never has to
     * guard for ``null``/``undefined``.
     */
    _validation?: Record<string, FieldValidation>;
    /**
     * Pages in the underlying PDF, ``null`` for non-PDF docs or unreadable
     * blobs. Backed by ``pypdf`` on the backend (#755). Drives the
     * page-preview tile count in the expanded ``<Document>`` card.
     */
    page_count?: number | null;
}

export interface CorrectFactRequest {
    field_id: string;
    value: unknown;
    /** Validator id from ``_validation[field_id].validator_id`` so the
     * audit event can pin which rule the correction satisfied. */
    validator_id?: string;
}

export interface CorrectFactResponse {
    doc_id: string;
    field_id: string;
    value: unknown;
    rds_committed: boolean;
}

/** classifier-v3 (domain → role) taxonomy nodes (#1066). The legacy category/subcategory/type
 *  tree (+ its ``/v1/taxonomy/tree`` fetch and ``override-type`` payload) was retired in #1065. */
export interface TaxonomyRole {
    id: string;
    name: string;
    tier: number;
}

export interface TaxonomyDomain {
    id: string;
    name: string;
    roles: TaxonomyRole[];
}

/** classifier-v3 two-axis override body (#1066). The backend maps it to the legacy triple. */
export interface OverrideClassificationPayload {
    domain: string;
    role: string;
}

export const documentService = {
    /**
     * Upload a document for processing.
     */
    upload: async (
        file: File,
        comment: string = '',
        onProgress?: (progress: number) => void
    ): Promise<UploadResponse> => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('comment', comment);

        try {
            const response = await api.post<UploadResponse>('/user-documents/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent) => {
                    if (onProgress && progressEvent.total) {
                        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        onProgress(progress);
                    }
                },
            });

            return response.data;
        } catch (err) {
            // 409 from ``backend/src/api/v1/endpoints/documents/_crud.py``
            // is a content-hash dedup hit — the user already has this
            // exact file in their tenant. Surface it as a typed error so
            // ``uploadBatch`` can partition duplicates out of generic
            // failures and the documents page can render a meaningful
            // "already in your account" toast (with a deep link to the
            // existing doc when the backend returns ``existing_doc_id``).
            if (err instanceof AxiosError && err.response?.status === 409) {
                const detail = err.response.data?.detail;
                const existingDocId =
                    typeof detail === 'object' && detail !== null && 'existing_doc_id' in detail
                        ? String((detail as Record<string, unknown>).existing_doc_id ?? '') || undefined
                        : undefined;
                throw new DuplicateUploadError(file.name, existingDocId);
            }
            throw err;
        }
    },

    /**
     * Get job status by ID.
     */
    getJobStatus: async (jobId: string): Promise<JobStatus> => {
        const response = await api.get<JobStatus>(`/user-documents/jobs/${jobId}`);
        return response.data;
    },

    /**
     * Get batch job status for multiple jobs in a single request.
     * Reduces API calls when polling many jobs simultaneously.
     */
    getBatchJobStatus: async (jobIds: string[]): Promise<JobStatus[]> => {
        if (jobIds.length === 0) {
            return [];
        }
        const response = await api.post<{ jobs: JobStatus[] }>('/user-documents/jobs/batch', {
            job_ids: jobIds,
        });
        return response.data.jobs;
    },

    /**
     * Get job status by document IDs.
     * This is useful when documents don't have job_id stored (legacy data).
     */
    getJobsByDocIds: async (docIds: string[]): Promise<JobStatus[]> => {
        if (docIds.length === 0) {
            return [];
        }
        const response = await api.post<{ jobs: JobStatus[] }>('/user-documents/jobs/by-doc-ids', {
            doc_ids: docIds,
        });
        return response.data.jobs;
    },

    /**
     * List documents with pagination.
     */
    listDocuments: async (params?: ListDocumentsParams): Promise<ListDocumentsResponse> => {
        const response = await api.get<ListDocumentsResponse>('/user-documents', { params });
        return response.data;
    },

    /** Fetch all document IDs matching current filters (no pagination). For cross-page select-all. */
    getAllMatchingIds: async (params?: Omit<ListDocumentsParams, 'offset' | 'limit' | 'sort_by'>): Promise<string[]> => {
        const response = await api.get<{ ids: string[] }>('/user-documents/all-ids', { params });
        return response.data.ids;
    },

    /**
     * Resolve a deep-linked document to its page within the current feed (#1187).
     *
     * The feed is server-paginated, so ``?doc=<id>`` to a row beyond the first
     * page within its category never mounts. This returns the doc's 0-indexed
     * ``page`` (and ``offset``) within the same filtered + ordered result set, so
     * the caller can jump straight to it; ``found`` is false when the doc isn't in
     * the filtered set.
     */
    resolveDocumentPage: async (
        documentId: string,
        params?: ResolveDocumentPageParams,
    ): Promise<ResolveDocumentPageResponse> => {
        const response = await api.get<ResolveDocumentPageResponse>(
            `/user-documents/${documentId}/resolve-page`,
            { params },
        );
        return response.data;
    },

    /**
     * Get single document by ID.
     */
    getDocument: async (documentId: string): Promise<Document> => {
        const response = await api.get<Document>(`/user-documents/${documentId}`);
        return response.data;
    },

    /**
     * Delete a document.
     */
    deleteDocument: async (documentId: string): Promise<void> => {
        await api.delete(`/user-documents/${documentId}`);
    },

    reprocessDocument: async (documentId: string): Promise<{ doc_id: string; job_id: string }> => {
        const response = await api.post(`/user-documents/${documentId}/reprocess`);
        return response.data;
    },

    reprocessDocumentsBatch: async (documentIds: string[]): Promise<{ results: Array<{ doc_id: string; job_id: string; status: string }> }> => {
        const response = await api.post('/user-documents/reprocess/batch', { document_ids: documentIds });
        return response.data;
    },

    /**
     * Download a document as blob with filename from Content-Disposition header.
     */
    downloadDocument: async (documentId: string): Promise<{ blob: Blob; filename: string | null }> => {
        const response = await api.get(`/user-documents/${documentId}/download`, {
            responseType: 'blob',
        });

        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers['content-disposition'];
        let filename: string | null = null;
        if (contentDisposition) {
            // Parse: attachment; filename="smart_filename.pdf"
            const match = contentDisposition.match(/filename="?([^";\n]+)"?/i);
            if (match) {
                filename = match[1];
            }
        }

        return { blob: response.data, filename };
    },

    /**
     * URL builder for the per-page JPEG thumbnail (#755). The endpoint
     * is content-hash ETagged + immutable-cached, so the browser
     * re-uses the response across mounts without re-fetching.
     */
    getPageThumbnailUrl: (documentId: string, page: number, width: number = 240): string =>
        `/api/user-documents/${documentId}/page/${page}.jpg?width=${width}`,

    /**
     * Get document blob for preview viewer.
     * Returns a blob URL that can be used in iframe src or img src.
     */
    getDocumentForPreview: async (documentId: string): Promise<{ blobUrl: string; filename: string | null; blob: Blob }> => {
        const response = await api.get(`/user-documents/${documentId}/download?inline=true`, {
            responseType: 'blob',
        });

        const contentDisposition = response.headers['content-disposition'];
        let filename: string | null = null;
        if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^";\n]+)"?/i);
            if (match) {
                filename = match[1];
            }
        }

        const blob: Blob = response.data;
        const blobUrl = URL.createObjectURL(blob);
        return { blobUrl, filename, blob };
    },

    /**
     * Mark all documents as viewed (resets new docs counter).
     * Called when user clicks "New Documents (N)" to view new docs.
     */
    markDocumentsViewed: async (): Promise<void> => {
        await api.post('/user-documents/mark-viewed');
    },

    /**
     * FR-013 v2: enqueue async archive job. Worker streams the ZIP into S3,
     * the browser later fetches it via a short-lived presigned URL — bytes
     * never transit the BFF or backend pods on the download path.
     */
    startBatchDownloadV2: async (
        docIds: string[],
    ): Promise<{ job_id: string; status: string; total: number }> => {
        const response = await api.post<{ job_id: string; status: string; total: number }>(
            '/user-documents/batch-download-v2',
            { doc_ids: docIds },
        );
        return response.data;
    },

    /**
     * Poll progress for a v2 archive job. Used as a 5 s SSE-silence fallback.
     */
    getBatchDownloadProgressV2: async (
        jobId: string,
    ): Promise<{
        job_id: string;
        status: 'queued' | 'processing' | 'ready' | 'failed' | 'cancelled';
        progress: number;
        total: number;
        included_count: number;
        failed_count: number;
        error: string | null;
    }> => {
        const response = await api.get(`/user-documents/batch-download-v2/${jobId}/progress`);
        return response.data;
    },

    /**
     * Fetch the presigned S3 GET URL for a `ready` archive.
     * The URL has a short TTL (15 min default).
     */
    getBatchDownloadUrlV2: async (
        jobId: string,
    ): Promise<{
        download_url: string;
        filename: string;
        expires_at: string;
        included_count: number;
        failed_count: number;
    }> => {
        const response = await api.get(`/user-documents/batch-download-v2/${jobId}/url`);
        return response.data;
    },

    /**
     * Request cancellation of an in-flight v2 archive job.
     */
    cancelBatchDownloadV2: async (
        jobId: string,
    ): Promise<{ job_id: string; status: string }> => {
        const response = await api.post(`/user-documents/batch-download-v2/${jobId}/cancel`);
        return response.data;
    },

    /**
     * Restore a soft-deleted document.
     * Clears the deleted_at timestamp.
     */
    restoreDocument: async (documentId: string): Promise<void> => {
        await api.post(`/user-documents/${documentId}/restore`);
    },

    /**
     * Rotate a document by specified degrees (clockwise).
     * Persists rotation by modifying the actual file in storage.
     */
    rotateDocument: async (documentId: string, degrees: 90 | 180 | 270): Promise<void> => {
        await api.post(`/user-documents/${documentId}/rotate`, { degrees });
    },

    /**
     * List deleted documents for the "show deleted" view.
     */
    listDeletedDocuments: async (params?: Omit<ListDocumentsParams, 'view'>): Promise<ListDocumentsResponse> => {
        const response = await api.get<ListDocumentsResponse>('/user-documents', {
            params: { ...params, deleted_only: true },
        });
        return response.data;
    },

    /**
     * Get full metadata (extracted facts + classification) for a document.
     */
    getDocumentMetadata: async (documentId: string): Promise<DocumentMetadata> => {
        const response = await api.get<DocumentMetadata>(`/user-documents/${documentId}/metadata`);
        return response.data;
    },

    /**
     * Override the classifier-v3 two-axis (domain, role) classification and trigger reprocessing (#1066).
     * The backend validates the cell against taxonomy-v2 and reprocesses on the native cell (the legacy-triple
     * projection was removed with the classifier-v1 decommission, #2009).
     */
    overrideClassification: async (
        documentId: string,
        payload: OverrideClassificationPayload,
    ): Promise<{ doc_id: string; job_id: string; domain: string; role: string }> => {
        const response = await api.post(`/user-documents/${documentId}/override-classification`, payload);
        return response.data;
    },

    /**
     * Update extracted fact overrides for a document.
     *
     * #386 subtask 05: optional ``pickedEntityIdByField`` carries
     * autocomplete picks per fact field — operator selected an existing
     * canonical entity, so the worker should bypass L1-L4 dedup and merge
     * onto that entity directly. Keys are fact field ids (e.g. ``supplier_name``);
     * values are canonical entity ids.
     */
    updateFactOverrides: async (
        documentId: string,
        overrides: Record<string, string>,
        options?: { pickedEntityIdByField?: Record<string, string> },
    ): Promise<{ doc_id: string; extracted_facts: Record<string, unknown> }> => {
        const body: { overrides: Record<string, string>; picked_entity_id_by_field?: Record<string, string> } = {
            overrides,
        };
        if (options?.pickedEntityIdByField && Object.keys(options.pickedEntityIdByField).length > 0) {
            body.picked_entity_id_by_field = options.pickedEntityIdByField;
        }
        const response = await api.patch(`/user-documents/${documentId}/facts`, body);
        return response.data;
    },

    /**
     * #386 subtask 05 — autocomplete suggest scoped to one tenant + entity_type.
     *
     * Returns existing canonical entities whose display name prefix-matches
     * ``q``. Used by the document-fact-edit autocomplete control on entity-
     * mapped fields (e.g. ``supplier_name`` → ``supplier``). The picked
     * entity_id then flows through ``updateFactOverrides`` as a hard hint
     * to the worker recalc.
     */
    suggestEntities: async (
        entityType: string,
        q: string,
        limit: number = 10,
    ): Promise<{ items: Array<Record<string, unknown>> }> => {
        // `/v1/` is not optional here. The BFF proxies `/api/v1/*` generically
        // and serves only a few bare `/api/<x>` prefixes (`user-documents`
        // among them); `/api/twin/*` is not one, so the bare path 404'd and
        // every entity autocomplete came back empty (found by FR-017 #3's
        // "Verknüpfung hinzufügen", which reuses this call).
        const response = await api.get('/v1/twin/entities/suggest', {
            params: { entity_type: entityType, q, limit },
        });
        return response.data;
    },

    /**
     * Correct an extracted fact that L0 doc-side validation flagged
     * (issue #620 subtask 06). Distinct from ``updateFactOverrides``:
     * - emits ``document.fact_corrected`` (not ``fact_overridden``);
     * - publishes a recalc with ``source="document_fact_corrected"`` so
     *   the worker's ``classify_updates`` OR-widened branch picks it up
     *   for targeted twin re-link.
     *
     * Use this when the field has a ``_validation`` entry; use
     * ``updateFactOverrides`` for unconstrained edits.
     */
    correctFact: async (
        documentId: string,
        payload: CorrectFactRequest,
    ): Promise<CorrectFactResponse> => {
        const response = await api.post<CorrectFactResponse>(
            `/user-documents/${documentId}/facts/correct`,
            payload,
        );
        return response.data;
    },

    /**
     * Get the classifier-v3 (domain → role) taxonomy tree for the lens nav (#1066).
     * Empty when the v2 config isn't deployed (legacy-only tenant).
     */
    getTaxonomyDomains: async (): Promise<TaxonomyDomain[]> => {
        const response = await api.get<{ domains: TaxonomyDomain[] }>('/v1/taxonomy/domains');
        return response.data.domains;
    },
};

export type { UploadResponse, JobStatus, Document, ListDocumentsResponse, ListDocumentsParams, ParentSource, SortBy, ViewMode, CategoryCount, SubcategoryCount };
